import React, { useState, useEffect } from 'react';

const CustomAlert = ({ 
  isOpen, 
  onClose, 
  title = "Alert", 
  message, 
  type = "info", // info, success, warning, error
  showCancel = false,
  onConfirm,
  confirmText = "OK",
  cancelText = "Cancel"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    handleClose();
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          iconColor: 'text-green-500',
          borderColor: 'border-green-500',
          bgColor: 'bg-green-50',
          buttonColor: 'bg-green-500 hover:bg-green-600'
        };
      case 'warning':
        return {
          icon: '⚠',
          iconColor: 'text-yellow-500',
          borderColor: 'border-yellow-500',
          bgColor: 'bg-yellow-50',
          buttonColor: 'bg-yellow-500 hover:bg-yellow-600'
        };
      case 'error':
        return {
          icon: '✕',
          iconColor: 'text-red-500',
          borderColor: 'border-red-500',
          bgColor: 'bg-red-50',
          buttonColor: 'bg-red-500 hover:bg-red-600'
        };
      default:
        return {
          icon: 'ℹ',
          iconColor: 'text-blue-500',
          borderColor: 'border-blue-500',
          bgColor: 'bg-blue-50',
          buttonColor: 'bg-blue-500 hover:bg-blue-600'
        };
    }
  };

  const styles = getTypeStyles();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !mt-0">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${styles.borderColor} ${styles.bgColor} rounded-t-lg`}>
          <div className="flex items-center">
            <span className={`text-2xl mr-3 ${styles.iconColor}`}>
              {styles.icon}
            </span>
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {typeof message === 'string' ? (
            <p className="text-gray-700 whitespace-pre-line break-words">
              {message}
            </p>
          ) : (
            <div className="text-gray-700 break-words">
              {message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
          {showCancel && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${styles.buttonColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomAlert;

